import {
	DataType,
	SNOWAttributeType,
	SnowAttribute,
	TaskDefinition,
	WorkflowDefinition,
	WorkflowIntention,
} from '@amzn/snow-nxt-workflow-service-typescript-client';
import RequirementForm from './steps/RequirementForm';
import React from 'react';
import {SpaceBetween, WizardProps} from '@amzn/awsui-components-react-v3';
import ReviewForm from './steps/ReviewForm';
import DiffReviewForm from './steps/DiffReviewForm';
import {SNOWEntity} from 'src/@types/snowDataServiceTypes/createEntity.interface';
import {WorkflowStatus} from 'src/constants/commonEnums';
import katalMetricsPublisher from 'src/utils/metrics';
import {IBFR_WORKFLOW_NAME} from 'src/config';
import {SubWorkflowTaskGroup} from './subWorkflowRequirementUtils';
import {
	isAttributeMandatory,
	isEmptyAttributeValue,
	isTaskSpecificAttribute,
	isWorkflowSpecificAttribute,
} from 'src/utils/attributeTypeUtils';

export const missingAttributeMetricPublisher =
	katalMetricsPublisher.newChildActionPublisherForMethod(
		'AttributeMissingPublisher'
	);

export const filterWorkflowLevelRequirements = (
	taskAttributes: SnowAttribute[] = [],
) => taskAttributes?.filter(
	(taskAttribute) =>
		isWorkflowSpecificAttribute(taskAttribute) && !taskAttribute.value
);


export const filterTaskSpecificRequirements = (
	attributes: SnowAttribute[] | null = []
) => (attributes ?? []).filter(isTaskSpecificAttribute);

/**
 * Collapses attributes shared across tasks into one entry per attributeId.
 * The shared field registers a single value and a single required rule, so the
 * mandatory flags are unioned rather than overwritten: the field is optional
 * only when every task declaring it says it is optional.
 */
export const mergeCommonRequirements = (attributes: SnowAttribute[] = []) => {
	const merged = new Map<string | undefined, SnowAttribute>();

	attributes.forEach((attribute) => {
		const existing = merged.get(attribute.attributeId);

		merged.set(
			attribute.attributeId,
			existing
				? {
					...existing,
					mandatory: isAttributeMandatory(existing) || isAttributeMandatory(attribute),
				}
				: attribute
		);
	});

	return merged;
};

export const generateRequirements = (
	taskDefinitions: TaskDefinition[],
	workflowType: string,
	entityType: string,
) => {
	const requirements: SNOWEntity = {
		EntityName: '',
		WorkflowType: workflowType,
		Status: WorkflowStatus.DRAFT,
		EntityData: {},
		Region: '',
		GroupId: 'SelfServeWorkflows',
		EntityType: entityType,
	};

	let workflowLevelRequirements = {} as { EntityType?: string };

	workflowLevelRequirements = [
		...new Set(
			taskDefinitions
				.map((taskDefinition) =>
					filterWorkflowLevelRequirements(taskDefinition.taskInputs)
				)
				.flat()
		),
	].reduce(
		(o, requirement) => ({
			...o,
			...(requirement.attributeName
				? { [requirement.attributeName]: getRequirementValue(requirement) }
				: {}),
		}),
		workflowLevelRequirements
	);

	workflowLevelRequirements.EntityType =
		workflowLevelRequirements.EntityType || entityType;
	requirements['EntityData'] = workflowLevelRequirements;

	let taskRequirements = {};

	taskDefinitions.forEach((taskDefinition) => {
		if (taskDefinition.taskId)
			taskRequirements = {
				...taskRequirements,
				[taskDefinition.taskId]: filterTaskSpecificRequirements(
					taskDefinition.taskInputs
				).reduce(
					(o, requirement) => ({
						...o,
						...(requirement.attributeName
							? { [requirement.attributeName]: getRequirementValue(requirement) }
							: {}),
					}),
					{}
				),
			};
	});

	return { ...requirements, ...taskRequirements };
};

const getRequirementValue = ({ dataType, value }: SnowAttribute) => {
	switch (dataType) {
	case DataType.STRING:
		return value ? value : '';
	case DataType.NUMBER:
		return value ? value : '0';
	case DataType.BOOLEAN:
		return value ? value : 'false';
	default:
		return value ?? '';
	}
};

export enum IFBRValidationStatus {
	Loading,
	NotStarted,
	Success,
	Error,
	QueryFailed,
	ValidationFailed,
	NoConfigFound
}

export interface TaskStepDefinition {
	taskName: string | null | undefined;
	taskId: string | null | undefined;
	requirements: SnowAttribute[];
}
//TODO : Remove this after enforcing these attributes selection in the workflow creation step.
const requiredAttributes = (attributeValue): SnowAttribute[] => [
	{
		attributeId: 'Region',
		attributeName: 'Region',
		attributeType: SNOWAttributeType.WORKFLOW_SPECIFIC_ATTRIBUTE,
		regex: '(NA|EU|JP)',
		description:
			'The region in which the entity is created. Must be NA | EU | JP ',
		value: attributeValue,
		helpText: 'NA | EU | JP',
		dataType: DataType.STRING,
	},
	{
		attributeId: 'EntityName',
		attributeName: 'Entity Name',
		attributeType: SNOWAttributeType.WORKFLOW_SPECIFIC_ATTRIBUTE,
		regex: '^\\S+$',
		description: 'The Entity Name of the Node',
		value: attributeValue,
		helpText: 'Eg: ABC1',
		dataType: DataType.STRING,
	},
	{
		attributeId: 'EntityType',
		attributeName: 'Entity Type',
		attributeType: SNOWAttributeType.WORKFLOW_SPECIFIC_ATTRIBUTE,
		regex: '^\\S+$',
		description: 'The Entity type of the Node',
		value: attributeValue,
		helpText: 'Eg: fc',
		dataType: DataType.STRING,
	},
];

const generateRequiredAttributes = (attributes, attributeId, attributeValue) => {
	if (
		!attributes ||
		attributes.length === 0 ||
		attributes.some((att) => att.attributeId === attributeId)
	)
		return [];

	missingAttributeMetricPublisher.publishCounterMonitor(
		`${attributeId}AttributeMissing`,
		1
	);

	return requiredAttributes(attributeValue)
		.flat()
		.filter((attribute) => attribute.attributeId === attributeId);
};

export const generateFormSteps = (
	taskDefinitions: TaskDefinition[] = [],
	workflowDefinition: WorkflowDefinition | undefined,
	activeStep: number,
	apiResponseData: any,
	previousValues?: Record<string, unknown>,
	workflowName?: string,
	handleAutoPopulation?: (attributeId: string) => void,
	enrichRequirements?: (requirements: any[]) => any[],
	disabledFields?: Set<string>,
	subWorkflowGroups: SubWorkflowTaskGroup[] = [],
) => {
	const workflowDescription = workflowDefinition?.description;
	const entityType = workflowDefinition?.entityType;

	const getTaskStepDefinitions = (definitions: TaskDefinition[]) => definitions
		.map((taskDefinition) => {
			const baseRequirements = filterTaskSpecificRequirements(taskDefinition.taskInputs);

			return {
				taskName: taskDefinition.taskName,
				taskId: taskDefinition.taskId,
				requirements: enrichRequirements ? enrichRequirements(baseRequirements) : baseRequirements,
			};
		})
		.filter((step) => step.requirements.length > 0);

	const taskStepsAttributes = getTaskStepDefinitions(taskDefinitions);

	// A sub-workflow task whose inputs all have values has nothing to ask, because its parent
	// supplied them, so it is left out of its step and of the review. A sub-workflow with no task
	// left gets no step.
	const subWorkflowSteps = subWorkflowGroups
		.map((group) => ({
			subWorkflowName: group.workflowName,
			taskSteps: getTaskStepDefinitions(group.taskDefinitions).filter((step) =>
				step.requirements.some((requirement) => isEmptyAttributeValue(requirement.value))
			),
		}))
		.filter((subWorkflowStep) => subWorkflowStep.taskSteps.length > 0);

	// The review shows every task on one step, so a sub-workflow's tasks are named with it.
	const allTaskStepDefinitions = [
		...taskStepsAttributes,
		...subWorkflowSteps.flatMap(({ subWorkflowName, taskSteps }) =>
			taskSteps.map((step) => ({ ...step, taskName: `[${subWorkflowName}] ${step.taskName}` }))
		),
	];

	const map = mergeCommonRequirements(
		[...taskDefinitions, ...subWorkflowGroups.flatMap((group) => group.taskDefinitions)]
			.flatMap((taskDefinition) => filterWorkflowLevelRequirements(taskDefinition.taskInputs))
	);

	const workflowCommonAttributes = [...map.values()].filter(
		(attribute) => attribute.attributeName !== 'EntityType'
	);

	const requiredAttributesMap = new Map<string, string | undefined>([
		['Region', undefined],
		['EntityName', undefined],
		['EntityType', entityType],
	]);

	const baseCommonStepAttributes = [
		...workflowCommonAttributes,
		...Array.from(requiredAttributesMap.entries()).flatMap(([key, value]) =>
			generateRequiredAttributes(workflowCommonAttributes, key, value)
		),
	];

	const commonStepAttributes = enrichRequirements ? enrichRequirements(baseCommonStepAttributes) : baseCommonStepAttributes;

	const steps: WizardProps.Step[] = [];

	const commonRequirementsStep: WizardProps.Step = {
		title: `Workflow Inputs for ${workflowName}`,
		description: workflowDescription,
		content: (
			<RequirementForm
				title={`Common Data for ${workflowName}`}
				requirements={commonStepAttributes}
				isIBFR={(workflowName === IBFR_WORKFLOW_NAME)}
				prefilledValues={apiResponseData}
				handleAutoPopulation={handleAutoPopulation}
				disabledFields={disabledFields}
			/>
		),
	};

	const buildTaskRequirementsStep = (
		title: string,
		taskSteps: TaskStepDefinition[]
	): WizardProps.Step => ({
		title,
		description: workflowDefinition?.description,
		content: (
			<SpaceBetween size="l">
				{taskSteps.map((taskStepsAttribute) => (
					<RequirementForm
						title={`${taskStepsAttribute.taskName}`}
						taskId={taskStepsAttribute.taskId}
						key={taskStepsAttribute.taskId}
						requirements={taskStepsAttribute.requirements}
						isIBFR={(workflowName === IBFR_WORKFLOW_NAME)}
						prefilledValues={apiResponseData}
						handleAutoPopulation={handleAutoPopulation}
						disabledFields={disabledFields}
					/>
				))}
			</SpaceBetween>
		),
	});

	const taskRequirementsStep = buildTaskRequirementsStep(
		`Task-Specific Inputs for ${workflowName}`,
		taskStepsAttributes
	);

	const subWorkflowRequirementsSteps = subWorkflowSteps.map(({ subWorkflowName, taskSteps }) =>
		buildTaskRequirementsStep(`Task-Specific Inputs for ${subWorkflowName}`, taskSteps)
	);

	const workflowIntention = workflowDefinition?.workflowIntention || WorkflowIntention.CREATE;

	const hasPreviousValues = previousValues && Object.keys(previousValues).length > 0;

	const reviewStep =
		workflowIntention === WorkflowIntention.UPDATE && hasPreviousValues
			? generateDiffReviewStep(
				workflowName,
				workflowDescription,
				workflowIntention,
				commonStepAttributes,
				allTaskStepDefinitions,
				previousValues
			)
			: generateStandardReviewStep(
				workflowName,
				workflowDescription,
				commonStepAttributes,
				allTaskStepDefinitions
			);

	return [
		...steps,
		commonRequirementsStep,
		...(taskStepsAttributes.length > 0 ? [taskRequirementsStep] : []),
		...subWorkflowRequirementsSteps,
		reviewStep,
	];
};


export const generateStandardReviewStep = (
	workflowName: string | null | undefined,
	workflowDescription: string | null | undefined,
	commonStepAttributes: SnowAttribute[],
	taskStepsAttributes: TaskStepDefinition[]
): WizardProps.Step => {
	return {
		title: `Review Information for ${workflowName}`,
		description: workflowDescription,
		content: (
			<SpaceBetween size="l">
				<ReviewForm
					header={`Workflow Inputs for ${workflowName}`}
					requirements={commonStepAttributes}
				/>
				{taskStepsAttributes.length > 0 &&
					taskStepsAttributes.map((taskStepsAttribute) => (
						<ReviewForm
							key={taskStepsAttribute.taskId}
							header={`Input Data for ${taskStepsAttribute.taskName}`}
							taskId={taskStepsAttribute.taskId}
							requirements={taskStepsAttribute.requirements}
						/>
					))}
			</SpaceBetween>
		),
	};
};


export const generateDiffReviewStep = (
	workflowName: string | null | undefined,
	workflowDescription: string | null | undefined,
	workflowIntention: WorkflowIntention,
	commonStepAttributes: SnowAttribute[],
	taskStepsAttributes: TaskStepDefinition[],
	previousValues: Record<string, unknown> = {}
): WizardProps.Step => {
	const isDeleteWorkflow = workflowIntention === WorkflowIntention.DELETE;
	const title = `Review ${isDeleteWorkflow ? 'Deletion' : 'Changes'} for ${workflowName}`;

	// For delete operations, show standard review without diff
	if (isDeleteWorkflow) {
		return generateStandardReviewStep(
			workflowName,
			workflowDescription,
			commonStepAttributes,
			taskStepsAttributes
		);
	}

	const editableCommonAttributes = commonStepAttributes.filter((attr) => !attr.value);
	const editableTaskStepsAttributes = taskStepsAttributes
		.map((step) => ({
			...step,
			requirements: step.requirements.filter((attr) => !attr.value),
		}))
		.filter((step) => step.requirements.length > 0);

	// For update operations, show diff review
	return {
		title,
		description: workflowDescription,
		content: (
			<SpaceBetween size="l">
				{editableCommonAttributes.length > 0 && (
					<DiffReviewForm
						header={`Workflow Inputs for ${workflowName}`}
						requirements={editableCommonAttributes}
						previousValues={previousValues}
					/>
				)}
				{editableTaskStepsAttributes.length > 0 &&
					editableTaskStepsAttributes.map((taskStepsAttribute) => {
						const taskId = taskStepsAttribute.taskId ?? '';
						const taskPreviousValues = taskId ? previousValues[taskId] : undefined;
						const taskPreviousValuesRecord: Record<string, unknown> =
							taskPreviousValues && typeof taskPreviousValues === 'object' && !Array.isArray(taskPreviousValues)
								? (taskPreviousValues as Record<string, unknown>)
								: {};

						return (
							<DiffReviewForm
								key={taskId}
								header={`Input Data for ${taskStepsAttribute.taskName}`}
								taskId={taskId}
								requirements={taskStepsAttribute.requirements}
								previousValues={taskPreviousValuesRecord}
							/>
						);
					})}
			</SpaceBetween>
		),
	};
};

export const getWorkflowStartRedirectionLink = (entity: string, workflowType: string) => {
	let link = `/findEntity?name=${entity}`;

	if (workflowType && workflowType.trim() !== '') {
		link += `&workflowType=${workflowType}`;
	}

	return link;
};
